import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import React from "react";
import VirtualizedCompanyDropdown, {
  CompanyOption,
} from "../VirtualizedCompanyDropdown";
import TierSelector from "../shared/TierSelector";

export interface GraphParameters {
  entityId: string;
  selectedCompany: CompanyOption | null;
  orgStructure: string;
  supplierTiers: number;
  customerTiers: number;
  layoutType: "force" | "hierarchy";
  showEdgeLabels: boolean;
  showNodeLabels: boolean;
}

interface GraphSettingsSectionProps {
  parameters: GraphParameters;
  onParameterChange: (key: keyof GraphParameters, value: any) => void;
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  disabled?: boolean;
}

const GraphSettingsSection: React.FC<GraphSettingsSectionProps> = ({
  parameters,
  onParameterChange,
  expanded,
  onChange,
  disabled = false,
}) => {
  const handleCompanySelection = (companies: CompanyOption[]) => {
    const selectedCompany = companies.length > 0 ? companies[0] : null;
    onParameterChange("selectedCompany", selectedCompany);
    onParameterChange("entityId", selectedCompany ? selectedCompany.id : "");
  };

  // Count only data/filter parameters, not display settings
  const activeCount =
    (parameters.entityId ? 1 : 0) +
    (parameters.orgStructure ? 1 : 0) +
    (parameters.supplierTiers > 0 ? 1 : 0) +
    (parameters.customerTiers > 0 ? 1 : 0);
  // Display settings (layoutType, showEdgeLabels, showNodeLabels) excluded from count

  return (
    <Accordion
      sx={{
        bgcolor: "background.paper",
        borderBottom: 1,
        borderColor: "divider",
        "&:before": { display: "none" },
      }}
      elevation={0}
      expanded={expanded}
      onChange={onChange}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2" fontWeight="medium">
          Graph Settings
          {activeCount > 0 && (
            <Chip
              label={activeCount}
              size="small"
              sx={{ ml: 1, height: 20, fontSize: "0.75rem" }}
            />
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Seed Entity Selection */}
          <Box>
            <VirtualizedCompanyDropdown
              label="Seed Entity"
              value={
                parameters.selectedCompany ? [parameters.selectedCompany] : []
              }
              onChange={handleCompanySelection}
              placeholder="Search for organizations..."
              multiple={false}
            />
          </Box>

          {/* Organization Structure */}
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel id="org-structure-label">
              Organization Structure
            </InputLabel>
            <Select
              labelId="org-structure-label"
              label="Organization Structure"
              value={parameters.orgStructure}
              onChange={(e) =>
                onParameterChange("orgStructure", e.target.value as string)
              }
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="parents_subsidiaries">
                Parents & Subsidiaries
              </MenuItem>
              <MenuItem value="parents">Parents</MenuItem>
              <MenuItem value="subsidiaries">Subsidiaries</MenuItem>
            </Select>
          </FormControl>

          {/* Supplier Tiers */}
          <TierSelector
            label="Supplier Tiers"
            value={parameters.supplierTiers}
            onChange={(value) => onParameterChange("supplierTiers", value)}
            min={0}
            max={5}
            disabled={disabled}
          />

          {/* Customer Tiers */}
          <TierSelector
            label="Customer Tiers"
            value={parameters.customerTiers}
            onChange={(value) => onParameterChange("customerTiers", value)}
            min={0}
            max={5}
            disabled={disabled}
          />

          {/* Performance Warning */}
          {(parameters.supplierTiers >= 2 || parameters.customerTiers >= 2) && (
            <Box
              sx={{
                p: 1.5,
                backgroundColor: "rgba(33, 150, 243, 0.1)",
                borderLeft: "3px solid",
                borderColor: "info.main",
                borderRadius: 1,
              }}
            >
              <Typography variant="caption" sx={{ color: "info.main" }}>
                <strong>Note:</strong> Higher tiers may result in longer load
                times for entities with large networks.
              </Typography>
            </Box>
          )}

          {/* Divider */}
          <Box sx={{ borderTop: 1, borderColor: "divider", my: 1 }} />

          {/* Layout Type */}
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel id="layout-label">Layout</InputLabel>
            <Select
              labelId="layout-label"
              label="Layout"
              value={parameters.layoutType}
              onChange={(e) =>
                onParameterChange(
                  "layoutType",
                  e.target.value as "force" | "hierarchy"
                )
              }
            >
              <MenuItem value="force">Force-Directed</MenuItem>
              <MenuItem value="hierarchy">Hierarchical</MenuItem>
            </Select>
          </FormControl>

          {/* Display Options */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={parameters.showEdgeLabels}
                  onChange={(e) =>
                    onParameterChange("showEdgeLabels", e.target.checked)
                  }
                  size="small"
                  disabled={disabled}
                />
              }
              label="Show Edge Labels"
            />
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={parameters.showNodeLabels}
                  onChange={(e) =>
                    onParameterChange("showNodeLabels", e.target.checked)
                  }
                  size="small"
                  disabled={disabled}
                />
              }
              label="Show Node Labels"
            />
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default GraphSettingsSection;
